/* Jane Lushka — motion engine for the Apple refinement layer.
   1. html.jl-scrolled  -> frosted header past 140px scroll
   2. Scroll-reveal: curated elements fade+rise once when entering viewport,
      with a small stagger per sibling group. Respects reduced motion.
   Perf notes: reads (getBoundingClientRect) and writes (classList) are kept in
   separate phases to avoid forced reflows, mutations are batched per frame, and
   reveal classes are removed after the transition so hover transitions and
   will-change don't linger on every card. */
(function () {
  var html = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  html.classList.add('jl-motion');

  /* ---------- frost the natively-sticky header on scroll ---------- */
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      html.classList.toggle('jl-scrolled', window.scrollY > 140);
      ticking = false;
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  if (reduced) {
    /* no reveals — and stop any Swiper autoplay the theme started (WCAG 2.2.2) */
    var stopAutoplay = function () {
      document.querySelectorAll('.swiper-container, .swiper').forEach(function (el) {
        if (el.swiper && el.swiper.autoplay && el.swiper.autoplay.running) {
          el.swiper.autoplay.stop();
        }
      });
    };
    [800, 2500].forEach(function (t) { setTimeout(stopAutoplay, t); });
    return;
  }

  /* ---------- scroll reveal ---------- */
  var SELECTORS = [
    '.content-block',
    '.jl-fabric-story__item',
    '.jl-fabric-hero__content',
    '.jl-usp-banner__item',
    '.jl-editorial-tile',
    '.product-card',
    '.limespot-recommendation-box-item'
  ].join(',');

  /* once the entrance transition finishes, drop the reveal classes so the
     element returns to its natural state (restores the 0.5s hover lift and
     releases the will-change compositor layer) */
  function cleanup(el) {
    el.addEventListener('transitionend', function handler(e) {
      /* transitionend bubbles — only react to the element's own reveal finishing */
      if (e.target !== el) return;
      el.classList.remove('jl-reveal', 'jl-in');
      el.style.transitionDelay = '';
      el.removeEventListener('transitionend', handler);
    });
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      var parent = el.parentElement;
      var idx = 0;
      if (parent) {
        var sibs = Array.prototype.filter.call(parent.children, function (c) {
          return c.classList && c.classList.contains('jl-reveal');
        });
        idx = sibs.indexOf(el.closest('.jl-reveal') || el);
        if (idx < 0) idx = 0;
      }
      el.style.transitionDelay = Math.min(idx % 6, 5) * 55 + 'ms';
      cleanup(el);
      el.classList.add('jl-in');
      io.unobserve(el);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -4% 0px' });

  function prime(root) {
    if (!root.querySelectorAll) return;
    var els = [];
    root.querySelectorAll(SELECTORS).forEach(function (el) {
      if (el.dataset.jlPrimed) return;
      el.dataset.jlPrimed = '1';
      els.push(el);
    });
    if (root !== document && root.matches && root.matches(SELECTORS) && !root.dataset.jlPrimed) {
      root.dataset.jlPrimed = '1';
      els.push(root);
    }
    if (!els.length) return;
    /* phase 1: all reads — no layout writes in between, so a single reflow */
    var vh = window.innerHeight * 0.9;
    var inView = els.map(function (el) {
      var r = el.getBoundingClientRect();
      return r.top < vh && r.bottom > 0;
    });
    /* phase 2: all writes */
    els.forEach(function (el, i) {
      /* elements already in the initial viewport appear instantly — no entry flash */
      if (inView[i]) return;
      el.classList.add('jl-reveal');
      io.observe(el);
    });
  }

  /* batch DOM-insertion bursts (carousels, drawer re-renders, autocomplete)
     into one prime() pass per frame */
  var pending = [];
  var scheduled = false;
  function flush() {
    scheduled = false;
    var batch = pending;
    pending = [];
    batch.forEach(prime);
  }
  var mo = new MutationObserver(function (muts) {
    muts.forEach(function (m) {
      m.addedNodes.forEach(function (n) {
        if (n.nodeType === 1) pending.push(n);
      });
    });
    if (pending.length && !scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  });

  function rescueStranded() {
    var els = document.querySelectorAll('.jl-reveal:not(.jl-in)');
    if (!els.length) return;
    /* same two-phase pattern as prime() */
    var hits = [];
    els.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0 && r.width > 0) hits.push(el);
    });
    hits.forEach(function (el) {
      el.style.transitionDelay = '0ms';
      cleanup(el);
      el.classList.add('jl-in');
    });
  }

  function init() {
    prime(document);
    mo.observe(document.body, { childList: true, subtree: true });
    [1500, 4000, 8000].forEach(function (t) { setTimeout(rescueStranded, t); });
    window.addEventListener('scroll', (function () {
      var last = 0;
      return function () {
        var now = Date.now();
        if (now - last > 1200) {
          last = now;
          requestAnimationFrame(rescueStranded);
        }
      };
    })(), { passive: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
